import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { uploadChatAttachment, type UploadedAttachment } from '../../api/files';
import { MAX_ATTACHMENTS } from '../../lib/attachments';

/**
 * useAttachmentUpload — file picker → presigned upload → MinIO PUT → finalize 의 3-step
 * 오케스트레이션을 hook 으로 캡슐화. chat MessageInput 의 정착 logic 을 도메인 중립 hook 으로
 * 추출 (pattern-shared-component-extraction-cross-domain-7-atomic 적용 — 2번째 정착 사례).
 *
 * 사용처:
 *   - chat MessageInput (chat-server attachments)
 *   - mail ComposeModal (mail compose attachments — 본 사이클 신규)
 *   - 향후 board / calendar 의 첨부 도입 시 동일 hook 재사용
 *
 * 안전 메커니즘:
 *   - AbortController unmount cleanup (pattern-react-sse-hook-with-cancelled-flag 적용)
 *   - clearAttachments() 가 inflight upload 도 abort
 *   - error 는 사용자 친화 메시지 (Error.message 그대로 — 향후 i18n key 매핑은 별도 사이클)
 */
export interface UseAttachmentUploadOptions {
  /** error 발생 시 호출되는 caller side 핸들러 (선택). hook 자체에도 error state 노출. */
  onError?: (msg: string) => void;
  /**
   * 게시판 첨부의 용량 귀속 (2026-08-19, 선택). 넘기면 서버가 그 게시판의 소유 스코프
   * (회사·팀·개인)로 한도를 검사하고 파일에도 같은 스코프를 찍는다.
   * 미지정이면 종전대로 업로더 개인 용량 — 채팅·메일·캘린더 호출부는 무변경이다.
   */
  boardId?: string;
  /**
   * 이미 붙어 있는 첨부로 시작한다 (2026-08-19, 선택) — 글 **수정** 화면용.
   * 첫 렌더에만 쓰는 씨앗이다. 이후 목록은 사용자가 더하고 빼는 대로 간다
   * (여기 값이 바뀌어도 다시 심지 않는다 — 편집 중이던 목록을 되돌리면 안 되므로).
   */
  initialAttachments?: readonly UploadedAttachment[];
  /**
   * 이 화면에서만 더 적게 받는다 (2026-09-10, 선택 · 기본은 공통 `MAX_ATTACHMENTS`).
   *
   * ★★★ AI 컴포저가 쓴다. 서버가 한 질문에 **다섯 개까지만** 읽는데
   *   (`assist-attachments.ts` 의 `ASSIST_MAX_FILES`) 화면이 열 개를 받으면
   *   **여섯째부터 조용히 버려진다.** 「붙였는데 안 본다」가 되는 자리라 화면에서 막는다.
   */
  maxItems?: number;
}

/**
 * 올리다 실패한 파일 (2026-08-23).
 *
 * 여태 실패는 `error` 한 줄로만 남고 **그 파일이 목록에서 사라졌다.** 여럿을 한꺼번에 올릴 때
 * 어느 것이 실패했는지 알 수 없고, 다시 시도하려면 처음부터 다시 골라야 했다.
 * 원본 File 을 들고 있으므로 **그 자리에서 다시 올릴 수 있다.**
 */
export interface FailedUpload {
  /** 목록 안에서만 쓰는 임시 id — 파일 번호가 없다(올라가지 못했으므로). */
  id: string;
  name: string;
  sizeBytes: number;
  message: string;
  file: File;
}

export interface UseAttachmentUploadReturn {
  attachments: UploadedAttachment[];
  /** 이 화면의 상한 — 화면이 「몇 개까지」를 말할 때 쓴다. */
  maxItems: number;
  /** 올리다 실패한 것들. 카드로 남아 **다시 시도**를 준다. */
  failed: FailedUpload[];
  /** 실패한 카드를 다시 올린다. 성공하면 목록에서 빠진다. */
  retryFailed: (id: string) => Promise<void>;
  /** 실패한 카드를 그냥 치운다. */
  dismissFailed: (id: string) => void;
  uploadingFile: string | null;
  uploadPercent: number;
  error: string | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleAttachClick: () => void;
  handleFileSelected: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  /**
   * 파일 여럿을 한 번에 올린다 (2026-08-22).
   *
   * `handleFileSelected` 가 이제 이것을 부른다 — 여태 고른 파일 중 **첫 개만** 올렸다.
   * `<input>` 에 `multiple` 이 없는 기존 호출부(채팅·게시판)에서는 어차피 한 개뿐이라
   * 지금까지와 결과가 같다.
   *
   * 하나씩 차례로 올린다 — 한꺼번에 밀어 넣으면 진행률이 뒤섞이고 상한 검사가 어긋난다.
   */
  addFiles: (files: readonly File[]) => Promise<void>;
  /**
   * **이미 올라와 있는** 파일을 첨부로 얹는다 (2026-08-22) — 워크스페이스에서 고른 것.
   * 올리지 않으므로 진행률도 용량도 늘지 않는다. 상한과 중복은 여기서 거른다.
   */
  addExisting: (items: readonly UploadedAttachment[]) => void;
  handleRemoveAttachment: (fileId: string) => void;
  /** attachments 배열 + error reset + inflight abort. caller 가 close/submit 후 호출. */
  clearAttachments: () => void;
}

export function useAttachmentUpload(options?: UseAttachmentUploadOptions): UseAttachmentUploadReturn {
  const maxItems = options?.maxItems ?? MAX_ATTACHMENTS;
  const [attachments, setAttachments] = useState<UploadedAttachment[]>(() => [...(options?.initialAttachments ?? [])]);
  const [failed, setFailed] = useState<FailedUpload[]>([]);
  // 실패 카드의 id 씨앗 — 파일 번호가 없으니 우리가 붙인다.
  const failSeq = useRef(0);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * 지금 몇 개인지는 **ref 로 센다** (2026-08-22).
   *
   * 여럿을 차례로 올릴 때 `attachments.length` 는 다음 렌더 전까지 안 바뀐다 — 그 값으로
   * 상한을 재면 열 개 제한이 있는데도 열두 개가 들어간다.
   */
  const countRef = useRef(attachments.length);
  useEffect(() => { countRef.current = attachments.length; }, [attachments.length]);

  const addFiles = useCallback(
    async (files: readonly File[]) => {
      for (const file of files) {
        // Loop1 F3 — UI maxItems 강제 (yaml SSOT 와 정합).
        if (countRef.current >= maxItems) {
          const msg = `첨부는 최대 ${maxItems}개까지 가능합니다`;
          setError(msg);
          options?.onError?.(msg);
          return;
        }
        // Loop1 F2 — rapid pick 시 이전 inflight controller dangling 차단.
        abortControllerRef.current?.abort();
        setUploadingFile(file.name);
        setUploadPercent(0);
        setError(null);
        const controller = new AbortController();
        abortControllerRef.current = controller;
        try {
          const uploaded = await uploadChatAttachment(file, {
            signal: controller.signal,
            onProgress: (p) => setUploadPercent(p.percent),
            boardId: options?.boardId,
          });
          countRef.current += 1;
          setAttachments((prev) => [...prev, uploaded]);
        } catch (err: unknown) {
          // Loop1 F1 — clearAttachments 후 AbortError 가 사용자에게 노출되는 "유령 토스트" 차단.
          if (controller.signal.aborted) return;
          const msg = err instanceof Error ? err.message : 'upload_failed';
          setError(msg);
          options?.onError?.(msg);
          // ★ 실패한 파일을 **남긴다** — 어느 것이 왜 실패했는지 보이고, 다시 시도할 수 있다.
          failSeq.current += 1;
          setFailed((prev) => [...prev, {
            id: `fail-${failSeq.current}`, name: file.name, sizeBytes: file.size, message: msg, file,
          }]);
          // 한 개가 실패해도 나머지는 계속 올린다 — 열 개 중 하나 때문에 다 잃을 이유가 없다.
        } finally {
          setUploadingFile(null);
          setUploadPercent(0);
          if (abortControllerRef.current === controller) {
            abortControllerRef.current = null;
          }
        }
      }
    },
    [options, maxItems],
  );

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      if (files.length === 0) return;
      await addFiles(files);
    },
    [addFiles],
  );

  const addExisting = useCallback((items: readonly UploadedAttachment[]) => {
    setAttachments((prev) => {
      const seen = new Set(prev.map((a) => a.fileId));
      const room = maxItems - prev.length;
      const fresh = items.filter((a) => !seen.has(a.fileId)).slice(0, Math.max(0, room));
      if (fresh.length < items.filter((a) => !seen.has(a.fileId)).length) {
        const msg = `첨부는 최대 ${maxItems}개까지 가능합니다`;
        setError(msg);
        options?.onError?.(msg);
      }
      if (fresh.length === 0) return prev;
      countRef.current = prev.length + fresh.length;
      return [...prev, ...fresh];
    });
  }, [options, maxItems]);

  const dismissFailed = useCallback((id: string) => {
    setFailed((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const retryFailed = useCallback(async (id: string) => {
    // 먼저 목록에서 뺀다 — 다시 실패하면 addFiles 가 새 카드로 넣는다(두 장이 되지 않는다).
    let target: FailedUpload | undefined;
    setFailed((prev) => {
      target = prev.find((f) => f.id === id);
      return prev.filter((f) => f.id !== id);
    });
    if (target) await addFiles([target.file]);
  }, [addFiles]);

  const handleRemoveAttachment = useCallback((fileId: string) => {
    setAttachments((prev) => prev.filter((a) => a.fileId !== fileId));
  }, []);

  const clearAttachments = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setAttachments([]);
    setFailed([]);
    setUploadingFile(null);
    setUploadPercent(0);
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  return {
    attachments,
    maxItems,
    failed,
    retryFailed,
    dismissFailed,
    uploadingFile,
    uploadPercent,
    error,
    fileInputRef,
    handleAttachClick,
    handleFileSelected,
    addFiles,
    addExisting,
    handleRemoveAttachment,
    clearAttachments,
  };
}
